// Incident Labs catalogue (solved runs are replayed on the server — Phase 3 — and the first verified
// solve of each incident earns incident XP once — Phase 4, src/core/xp.js incidentXp).
//
// An incident is a troubleshooting scenario that runs OUTSIDE the D01–D07 curriculum. It uses the
// mission format (network(), setup(rt), stages with ticket / evidence / tasks / hints, review) so
// the existing LabRuntime, simulator, consoles, workspace and checks run it unchanged. On top of
// that it carries `incident` metadata:
//   severity      P1–P3 (the ticket priority)
//   tier          2 NOC Technician · 3 Network Engineer · 4 Senior / Escalation (TIERS below;
//                 tier 1 is the Level 1 Support Desk, which is not an incident)
//   related       the domain that must be COMPLETE before the incident unlocks (local progress,
//                 like mission unlocks; admin test mode opens everything)
//   exam          "builds toward" labels (EXAMS below) — information only, never a gate
//
// Identity: the runtime is created with domain id INCIDENT_DOMAIN ('inc'), so an incident's lab
// state lives under lab:inc.<id> — it can never collide with a curriculum key (lab:dNN.uNN) and
// is not in the curriculum. Completion is recorded by src/core/incidentProgress.js, never in
// `progress`. Phase 3: the server verifier (src/core/labVerify.js) also replays solved runs of the
// allow-listed ids (SERVER_INCIDENT_IDS, exact key inc.<id>); never counted as missions.
import accessVlan from './access-vlan.js';
import trunkVlan from './trunk-vlan.js';
import etherchannel from './etherchannel.js';
import stpRoot from './stp-root.js';
import hsrpSplit from './hsrp-split.js';
import ospfAdjacency from './ospf-adjacency.js';
import ospfMtu from './ospf-mtu.js';
import bgpAdvertise from './bgp-advertise.js';
import ipsecTraffic from './ipsec-traffic.js';
import qosPeak from './qos-peak.js';
import vpnConnectivity from './vpn-connectivity.js';
import aclApp from './acl-app.js';
import aaaLogin from './aaa-login.js';
import segmentation from './segmentation.js';
import snmpTraps from './snmp-traps.js';
import syslogGap from './syslog-gap.js';
import ntpSync from './ntp-sync.js';
import dcCapstone from './dc-capstone.js';

export const INCIDENT_DOMAIN = 'inc';
/** Catalogue order = the order on the Incident Labs page (by tier, then by related domain). */
export const INCIDENTS = [accessVlan, trunkVlan, etherchannel, aclApp, snmpTraps, syslogGap, stpRoot, ospfAdjacency, ospfMtu, bgpAdvertise, vpnConnectivity, ipsecTraffic, qosPeak, hsrpSplit, aaaLogin, segmentation, ntpSync, dcCapstone];
export const TIERS = {
  2: { name: 'NOC Technician', blurb: 'One device, one clear fault, step-by-step hints.' },
  3: { name: 'Network Engineer', blurb: 'Compare two ends, a misleading symptom, hints that point at the layer.' },
  4: { name: 'Senior / Escalation', blurb: 'Design-level causes and policy fixes, minimal hints.' },
};
export const EXAMS = ['CCNA', 'CCNP ENCOR', 'CCNP ENARSI'];
const ID_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** The incident with this id, or null. */
export const findIncident = (id) => INCIDENTS.find((x) => x.id === id) || null;
/** 'ospf-adjacency' → 'inc.ospf-adjacency' (the same key format LabRuntime uses: lab:<key>). */
export const incidentKey = (id) => `${INCIDENT_DOMAIN}.${id}`;
/** True for a well-formed incident id (lowercase words joined by hyphens). */
export const isIncidentId = (id) => typeof id === 'string' && ID_RE.test(id);
/** The ticket of an incident's first stage (shown on the Incident Labs page). */
export const ticketOf = (inc) => inc.stages.find((s) => s.ticket)?.ticket || null;
